import { useQueryClient } from '@tanstack/react-query';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Alert, Box, Button, Typography, Stack, useTheme } from '@mui/material';
import BannerAppSettings, { emptyBannerAppConfig } from './BannerAppSettings';
import BannerFeedPicker from './BannerFeedPicker';
import { objectStorageApi } from '../../../api/objectStorage';
import { useNavigate, useParams } from 'react-router-dom';
import { adminApi } from '../../../api/admin';
import api from '../../../api/api';
import PageLayout from '../../../components/layout/PageLayout';
import ThumnailEditor from '../../../components/common/ThumnailEditor';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import useAdminDraftGuard, { AdminDraftProvider } from '../useAdminDraftGuard';
import BasicButton from '../../../components/button/BasicButton';
import CustomTextField from '../../../components/common/CustomTextField';
import ProgressBar from '../../../components/common/ProgressBar';
import { useUserStore } from '../../../stores/userStore';

const validHttpUrl = (value) => {
  if (!value) return true;
  try {
    const url = new URL(value);
    return (
      ['http:', 'https:'].includes(url.protocol) &&
      Boolean(url.hostname) &&
      !url.username &&
      !url.password
    );
  } catch {
    return false;
  }
};
const schema = yup.object().shape({
  title: yup.string().trim().required('제목을 입력하세요.'),
  titleEn: yup.string(),
  url: yup
    .string()
    .trim()
    .required('URL을 입력하세요.')
    .test('is-url', '올바른 URL 형식을 입력하세요.', (value) => {
      return validHttpUrl(value);
    }),
  urlEn: yup
    .string()
    .trim()
    .test('is-url', '올바른 URL 형식을 입력하세요.', (value) => {
      return validHttpUrl(value);
    }),
  imageUrlEn: yup
    .string()
    .trim()
    .test('is-url', '올바른 URL 형식을 입력하세요.', (value) => {
      return validHttpUrl(value);
    }),
});

const BannerForm = () => {
  const theme = useTheme();
  const live = useRef(true);
  const pending = useRef(false);
  const uploaded = useRef(new WeakMap());
  const [saving, setSaving] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);
  const { id } = useParams();
  const isEditMode = !!id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [deviceType, setDeviceType] = useState('MOBILE');

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      title: '',
      titleEn: '',
      url: '',
      urlEn: '',
      imageUrlEn: '',
    },
  });

  const [thumbnailImage, setThumbnailImage] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState('');
  const [imageError, setImageError] = useState('');
  const [isLoading, setIsLoading] = useState(isEditMode);
  const [appConfig, setAppConfig] = useState({ ...emptyBannerAppConfig });
  const [appFile, setAppFile] = useState(null);
  const [saveError, setSaveError] = useState('');
  const [loadFailed, setLoadFailed] = useState(false);
  const [cropBusy, setCropBusy] = useState(false);
  const [cropEnabled, setCropEnabled] = useState(true);
  const [thumbnailFilePreview, setThumbnailFilePreview] = useState('');
  const [appFilePreview, setAppFilePreview] = useState('');
  useEffect(() => {
    const thumbnailUrl = thumbnailImage ? URL.createObjectURL(thumbnailImage) : '';
    const appUrl = appFile ? URL.createObjectURL(appFile) : '';
    setThumbnailFilePreview(thumbnailUrl);
    setAppFilePreview(appUrl);
    return () => {
      if (thumbnailUrl) URL.revokeObjectURL(thumbnailUrl);
      if (appUrl) URL.revokeObjectURL(appUrl);
    };
  }, [thumbnailImage, appFile]);
  const representativePreview = thumbnailFilePreview || thumbnailPreview;
  const rollingPreview = appFilePreview || appConfig.imageUrl || representativePreview;
  const draft = useAdminDraftGuard(
    { fields: watch(), deviceType, thumbnailImage, thumbnailPreview, appConfig, appFile },
    { enabled: !isLoading && !loadFailed, busy: saving || isSubmitting || cropBusy },
  );
  const startCrop = useCallback(() => {
    setThumbnailImage(null);
    setThumbnailPreview('');
    setAppFile(null);
    setAppConfig((previous) => ({ ...previous, imageUrl: '' }));
  }, []);
  const selectFeed = ({ ko, en }) => {
    setCropEnabled(true);
    const url = `${window.location.origin}/feed/${encodeURIComponent(ko.slug)}`;
    setValue('title', ko.title);
    setValue('titleEn', en.title);
    setValue('url', url);
    setValue('urlEn', url);
    setValue('imageUrlEn', '');
    setThumbnailImage(null);
    setThumbnailPreview('');
    setAppFile(null);
    setAppConfig((previous) => ({
      ...previous,
      feedPostId: ko.id,
      feedSlug: ko.slug,
      feedCopyMode: 'auto',
      feedImageSource: ko.imageUrl,
      imageUrl: '',
      headline: ko.title.slice(0, 160),
      headlineEn: en.title.slice(0, 160),
      subtitle: ko.excerpt.slice(0, 300),
      subtitleEn: en.excerpt.slice(0, 300),
      cropX: 0.5,
      cropY: 0.5,
      cropZoom: 1,
      showAd: false,
      sourceIcon: 'none',
      fullCreative: true,
    }));
  };

  // 편집 모드일 때 기존 데이터 로드
  useEffect(() => {
    let active = true;
    if (isEditMode) {
      setIsLoading(true);
      setLoadFailed(false);
      const fetchBanner = async () => {
        try {
          const response = await adminApi.getBanner(id);
          if (!active || !live.current) return;
          const banner = response.data;
          if (
            !banner ||
            String(banner.id) !== id ||
            !['MOBILE', 'PC'].includes(banner.deviceType) ||
            typeof banner.title !== 'string' ||
            typeof banner.url !== 'string' ||
            (banner.appConfig != null &&
              (typeof banner.appConfig !== 'object' || Array.isArray(banner.appConfig)))
          )
            throw new Error('배너 응답을 확인하지 못했습니다.');
          setDeviceType(banner.deviceType);
          setAppConfig(
            banner.appConfig
              ? { ...emptyBannerAppConfig, ...banner.appConfig }
              : {
                  ...emptyBannerAppConfig,
                  appOnly: false,
                  headline: banner.title,
                  fullCreative: false,
                },
          );

          setValue('title', banner.title);
          setValue('titleEn', banner.titleEn || '');
          setValue('url', banner.url);
          setValue('urlEn', banner.urlEn || '');
          setValue('imageUrlEn', banner.imageUrlEn || '');

          if (banner.imageUrl) {
            setThumbnailPreview(banner.imageUrl);
          }
        } catch (error) {
          if (active && live.current) setLoadFailed(true);
        } finally {
          if (active && live.current) setIsLoading(false);
        }
      };

      fetchBanner();
    }
    return () => {
      active = false;
    };
  }, [id, isEditMode, setValue, reloadKey]);

  const handleThumbnailUpload = (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (file) {
      if (appConfig.feedPostId) {
        if (
          !['image/png', 'image/jpeg', 'image/webp'].includes(file.type) ||
          file.size > 5 * 1024 * 1024
        ) {
          setImageError('PNG/JPG/WebP 5MB 이하 이미지를 선택해주세요.');
          return;
        }
        // A feed banner has one rolling image, even though legacy web/app fields are separate.
        setCropEnabled(false);
        setAppFile(null);
        setAppConfig((previous) => ({ ...previous, imageUrl: '' }));
      }
      setThumbnailImage(file);
      setThumbnailPreview('');
      setImageError('');
    }
  };

  const handleThumbnailDelete = () => {
    if (appConfig.feedPostId) {
      setCropEnabled(false);
      setAppFile(null);
      setAppConfig((previous) => ({ ...previous, imageUrl: '' }));
    }
    setThumbnailImage(null);
    setThumbnailPreview('');
  };

  const handleAppFileChange = (file) => {
    setCropEnabled(false);
    setAppFile(file);
    if (file && appConfig.feedPostId) {
      setThumbnailImage(null);
      setThumbnailPreview('');
      setAppConfig((previous) => ({ ...previous, imageUrl: '' }));
      setImageError('');
    }
  };

  const onSubmit = async (formData) => {
    if (!live.current || pending.current || cropBusy || isLoading || loadFailed) return;
    if (
      appConfig.feedPostId &&
      !appFile &&
      !appConfig.imageUrl &&
      !thumbnailImage &&
      !thumbnailPreview
    ) {
      setSaveError('피드 배너의 크롭을 완료하거나 대표 이미지 또는 앱 이미지를 직접 선택해주세요.');
      return;
    }
    // 이미지 필수 검증 (생성 시에만, 편집 시에는 기존 이미지 유지 가능)
    if (!thumbnailImage && !thumbnailPreview && !appFile && !appConfig.imageUrl) {
      setImageError('대표 이미지를 선택하세요.');
      return;
    }

    pending.current = true;
    setSaving(true);
    try {
      setSaveError('');
      let savedAppConfig = appConfig;
      if (deviceType === 'MOBILE' && appFile) {
        let url = uploaded.current.get(appFile);
        if (!url) {
          const upload = new FormData();
          upload.append('image', appFile);
          const result = await objectStorageApi.uploadImage(upload);
          if (!live.current) return;
          url = result.data?.url;
          if (typeof url !== 'string' || !url || !validHttpUrl(url))
            throw new Error('앱 이미지 업로드 결과가 없습니다.');
          uploaded.current.set(appFile, url);
        }
        savedAppConfig = { ...appConfig, imageUrl: url };
      }
      // 썸네일 이미지 업로드 (새 이미지가 있는 경우에만)
      let imageUrl = savedAppConfig.feedPostId
        ? savedAppConfig.imageUrl || thumbnailPreview
        : thumbnailPreview;
      if (!imageUrl && deviceType === 'MOBILE') imageUrl = savedAppConfig.imageUrl;
      if (thumbnailImage) {
        imageUrl = uploaded.current.get(thumbnailImage);
        if (!imageUrl) {
          const imageFormData = new FormData();
          imageFormData.append('image', thumbnailImage);
          const { data: response } = await api.postAuth('/images/upload', imageFormData);
          if (!live.current) return;
          imageUrl = response?.data?.url;
          if (typeof imageUrl !== 'string' || !imageUrl || !validHttpUrl(imageUrl))
            throw new Error('대표 이미지 업로드 결과가 없습니다.');
          uploaded.current.set(thumbnailImage, imageUrl);
        }
      }
      if (savedAppConfig.feedPostId) savedAppConfig = { ...savedAppConfig, imageUrl };

      if (isEditMode) {
        // 배너 수정
        await adminApi.updateBanner(id, {
          ...(deviceType === 'MOBILE' ? { appConfig: savedAppConfig } : {}),
          title: formData.title,
          titleEn: formData.titleEn?.trim() || null,
          url: formData.url,
          urlEn: formData.urlEn?.trim() || null,
          imageUrl,
          imageUrlEn: formData.imageUrlEn?.trim() || null,
        });
      } else {
        // 배너 생성
        await adminApi.createBanner({
          ...(deviceType === 'MOBILE' ? { appConfig: savedAppConfig } : {}),
          title: formData.title,
          titleEn: formData.titleEn?.trim() || null,
          url: formData.url,
          urlEn: formData.urlEn?.trim() || null,
          imageUrl,
          imageUrlEn: formData.imageUrlEn?.trim() || null,
          deviceType,
        });
      }

      if (!live.current) return;
      void Promise.resolve(queryClient.invalidateQueries({ queryKey: ['home-banners'] })).catch(
        () => {},
      );
      draft.markClean();
      navigate('/admin?mainTab=ads&subTab=home-banner');
    } catch (error) {
      if (live.current)
        setSaveError(error.response?.data?.msg || error.message || '저장하지 못했습니다.');
    } finally {
      pending.current = false;
      if (live.current) setSaving(false);
    }
  };

  const handleCancel = () => {
    if (pending.current || !draft.confirmDiscard()) return;
    draft.markClean();
    navigate('/admin?mainTab=ads&subTab=home-banner');
  };

  return (
    <PageLayout>
      <ProgressBar isLoading={isLoading}>
        <Box
          sx={{
            maxWidth: '780px',
            mx: 'auto',
            minWidth: 0,
            overflowWrap: 'anywhere',
            '& .MuiTextField-root': { minWidth: 0 },
            '& .MuiAlert-root': { flexWrap: 'wrap' },
            '& .MuiAlert-message': { minWidth: 0, flex: '1 1 110px' },
          }}
        >
          {loadFailed && (
            <Alert
              severity="error"
              action={<Button onClick={() => setReloadKey((v) => v + 1)}>재시도</Button>}
            >
              배너를 불러오지 못했습니다. 다시 조회하거나 목록으로 돌아가주세요.
            </Alert>
          )}
          {saving && <Typography role="status">배너를 저장하는 중…</Typography>}
          <Stack
            component="fieldset"
            disabled={saving || isSubmitting || loadFailed}
            inert={saving || isSubmitting || loadFailed ? '' : undefined}
            spacing={{ xs: '30px', lg: '50px' }}
            sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}
          >
            {deviceType === 'PC' && (
              <Alert severity="info">
                이전 웹 전용 배너 기록입니다. 현재 앱·웹 공통 홈 배너에는 노출되지 않습니다.
              </Alert>
            )}

            {saveError && <Alert severity="error">{saveError}</Alert>}
            {deviceType === 'MOBILE' && (
              <BannerFeedPicker
                value={appConfig}
                onChange={setAppConfig}
                onSelect={selectFeed}
                onFileChange={setAppFile}
                onBusyChange={setCropBusy}
                onCropStart={startCrop}
                cropEnabled={cropEnabled}
                onCropRequest={() => setCropEnabled(true)}
              />
            )}
            <Stack spacing={'20px'}>
              <Stack spacing={'8px'}>
                <Typography fontSize={14} fontWeight={'regular'} color={theme.palette.black[7]}>
                  제목 <span style={{ color: theme.palette.red }}>*</span>
                </Typography>
                <CustomTextField
                  fullWidth
                  {...register('title')}
                  inputProps={{ 'aria-label': '제목' }}
                  error={!!errors.title}
                  helperText={errors.title?.message}
                  placeholder="제목을 입력하세요."
                />
              </Stack>
              <Stack spacing={'8px'}>
                <Typography fontSize={14} fontWeight={'regular'} color={theme.palette.black[7]}>
                  영문 제목 (선택)
                </Typography>
                <CustomTextField
                  fullWidth
                  {...register('titleEn')}
                  inputProps={{ 'aria-label': '영문 제목' }}
                  placeholder="비워두면 한국어 제목을 표시합니다."
                />
              </Stack>
              <Stack spacing={'8px'}>
                <Typography fontSize={14} fontWeight={'regular'} color={theme.palette.black[7]}>
                  URL <span style={{ color: theme.palette.red }}>*</span>
                </Typography>
                <CustomTextField
                  fullWidth
                  {...register('url')}
                  inputProps={{ 'aria-label': 'URL' }}
                  error={!!errors.url}
                  helperText={errors.url?.message}
                  placeholder="URL을 입력하세요."
                />
              </Stack>
              <Stack spacing={'8px'}>
                <Typography fontSize={14} fontWeight={'regular'} color={theme.palette.black[7]}>
                  영문 환경 URL (선택)
                </Typography>
                <CustomTextField
                  fullWidth
                  {...register('urlEn')}
                  inputProps={{ 'aria-label': '영문 환경 URL' }}
                  error={!!errors.urlEn}
                  helperText={errors.urlEn?.message}
                  placeholder="비워두면 한국어 환경 URL을 사용합니다."
                />
              </Stack>
              <Stack spacing={'8px'}>
                <Typography fontSize={14} fontWeight={'regular'} color={theme.palette.black[7]}>
                  대표 이미지 <span style={{ color: theme.palette.red }}>*</span>
                </Typography>
                <ThumnailEditor
                  thumbnailPreview={appConfig.feedPostId ? rollingPreview : representativePreview}
                  handleThumbnailUpload={handleThumbnailUpload}
                  handleThumbnailDelete={handleThumbnailDelete}
                  type={
                    appConfig.feedPostId
                      ? 'rollingBanner'
                      : deviceType === 'MOBILE'
                        ? 'mobileBanner'
                        : 'banner'
                  }
                />
                {appConfig.feedPostId && (
                  <Stack spacing={1}>
                    <Typography variant="body2">
                      대표 이미지를 교체하면 연결된 피드의 홈 롤링 이미지도 함께 변경됩니다. 피드
                      원본과 제목·설명 연동은 유지됩니다. PNG/JPG/WebP 5MB 이하를 사용해주세요.
                    </Typography>
                    <Button component="label" variant="outlined">
                      롤링 이미지 교체
                      <input
                        hidden
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        aria-label="피드 배너 이미지 교체"
                        onChange={handleThumbnailUpload}
                      />
                    </Button>
                  </Stack>
                )}
                {imageError && (
                  <Typography variant="body4" color={theme.palette.red}>
                    {imageError}
                  </Typography>
                )}
              </Stack>
              <Stack spacing={'8px'}>
                <Typography fontSize={14} fontWeight={'regular'} color={theme.palette.black[7]}>
                  영문 환경 이미지 URL (선택)
                </Typography>
                <CustomTextField
                  fullWidth
                  {...register('imageUrlEn')}
                  inputProps={{ 'aria-label': '영문 환경 이미지 URL' }}
                  error={!!errors.imageUrlEn}
                  helperText={errors.imageUrlEn?.message}
                  placeholder="비워두면 기본 대표 이미지를 사용합니다."
                />
              </Stack>
            </Stack>

            {/* 버튼 */}
            {deviceType === 'MOBILE' && (
              <BannerAppSettings
                value={appConfig}
                onChange={setAppConfig}
                file={appFile}
                onFileChange={handleAppFileChange}
                fallbackImage={representativePreview}
              />
            )}
            <Box width={'100%'} display={'flex'} justifyContent={'center'}>
              <Box display="flex" gap={1} width="100%" maxWidth={420} flexWrap="wrap">
                <BasicButton
                  fullWidth
                  variant="outlined"
                  onClick={handleCancel}
                  sx={{ flex: '1 1 100px', minWidth: 0, minHeight: '48px' }}
                >
                  취소
                </BasicButton>
                <BasicButton
                  fullWidth
                  type="submit"
                  disabled={saving || isSubmitting || loadFailed || cropBusy}
                  onClick={handleSubmit(onSubmit)}
                  sx={{ flex: '1 1 100px', minWidth: 0, minHeight: '48px' }}
                >
                  {isEditMode ? '수정' : '등록'}
                </BasicButton>
              </Box>
            </Box>
          </Stack>
          {loadFailed && <Button onClick={handleCancel}>목록으로 돌아가기</Button>}
        </Box>
      </ProgressBar>
    </PageLayout>
  );
};

export default function GuardedBannerForm() {
  const { id } = useParams();
  const user = useUserStore((state) => state.user);
  return (
    <AdminDraftProvider>
      <BannerForm key={JSON.stringify([id, user?.id, user?.role])} />
    </AdminDraftProvider>
  );
}
